<template>
    <div>
        <div>
            <div class="card card-default">
                <div class="card-header">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                        <span>
                            {{ $t('Personal Access Tokens') }}
                        </span>

                        <button
                            type="button"
                            v-if="auth_user && hasPermission(auth_user.can, 'create.user.security.api')"
                            class="btn btn-sm btn-soft-primary"
                            @click="toggleCreateTokenModal">
                            <i class="bi bi-plus-circle me-1"></i>
                            {{ $t('Create Token') }}
                        </button>
                    </div>
                </div>

                <div class="card-body">
                    <template v-if="isLoading">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('Loading') }}...
                    </template>
                    <!-- No Tokens Notice -->
                    <p class="mb-0" v-if="tokens.length === 0">
                        {{ $t('This user has not created any personal access tokens.') }}
                    </p>

                    <!-- Personal Access Tokens -->
                    <table class="table table-sm table-hover table-thead-bordered table-nowrap table-align-middle dataTable no-footer mb-0" v-if="tokens.length > 0">
                        <thead class="thead-light">
                        <tr>
                            <th>{{ $t('Name') }}</th>
                            <th>{{ $t('Expiration Date') }}</th>
                            <th></th>
                        </tr>
                        </thead>

                        <tbody>
                        <template v-for="(token, index) in tokens">
                            <tr role="row" :class="{'even': index % 2 === 0, 'odd': index % 2 !== 0 }">
                                <!-- Client Name -->
                                <td style="vertical-align: middle;">
                                    {{ token.name }}
                                </td>

                                <!-- Expiration Date -->
                                <td style="vertical-align: middle;">
                                    {{ moment(token.expires_at).format('MM/DD/YYYY') }}
                                </td>

                                <!-- Delete Button -->
                                <td style="vertical-align: middle; text-align: right;">
                                    <button
                                        class="btn btn-sm btn-soft-primary"
                                        v-if="auth_user && hasPermission(auth_user.can, 'delete.user.security.api')"
                                        type="button"
                                        @click="revoke(user_id, token.id)">
                                        <i class="bi bi-trash3"></i>
                                        {{ $t('Delete') }}
                                    </button>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <PersonalAccessTokensModal @close="toggleCreateTokenModal" :modalActive="createTokenModalActive">
            <template #header>
                <h4 class="modal-title">
                    {{ $t('Create Token') }}
                </h4>
            </template>
            <div class="modal-content">
                <!-- Create Token Form -->
                <form role="form" @submit.prevent="save()">
                    <!-- Name -->
                    <div class="form-group row">
                        <div class="col-12">
                            <input
                                id="create-token-name"
                                :placeholder="$t('Token Name')"
                                @change="validate"
                                type="text"
                                class="form-control form-control-lg"
                                name="name"
                                v-model.trim="form.name">
                        </div>
                        <div class="col-12 mt-2 text-danger" v-if="form.errors.length > 0">
                            {{ form.errors[0] }}
                        </div>
                    </div>

                    <!-- Scopes -->
                    <div class="form-group row" v-if="scopes.length > 0">
                        <label class="col-md-4 col-form-label">{{ $t('Scopes') }}</label>

                        <div class="col-md-6">
                            <div v-for="scope in scopes">
                                <div class="checkbox">
                                    <label>
                                        <input type="checkbox"
                                               @click="toggleScope(scope.id)"
                                               :checked="scopeIsAssigned(scope.id)">

                                        {{ scope.id }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <template #footer>
                <AppButton btnClass="btn-white"  @click="toggleCreateTokenModal">
                    <i class="bi bi-x-lg me-1"></i> {{ $t('Close') }}
                </AppButton>
                <AppButton btnClass="btn-primary" @click="save" :disabled="form.errors.length > 0">
                    <template v-if="isLoading">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('Loading') }}...
                    </template>
                    <template v-else>
                        <i class="bi bi-save me-1"></i>
                        {{ $t('Save') }}
                    </template>
                </AppButton>
            </template>
        </PersonalAccessTokensModal>

        <PersonalAccessTokensModal @close="toggleShowTokenModal" :modalActive="showTokenModalActive">
            <template #header>
                <h4 class="modal-title">
                    {{ $t('Personal Access Token') }}
                </h4>
            </template>
            <div class="modal-content">
                <p>
                    {{ $t(`messages.new_personal_access_token_warning`) }}
                </p>

                <textarea
                    v-on:focus="$event.target.select()"
                    ref="tokenTextArea"
                    readonly
                    :value="accessToken"
                    class="form-control" rows="10"></textarea>
            </div>
            <template #footer>
                <AppButton btnClass="btn-white"  @click="toggleShowTokenModal">
                    <i class="bi bi-x-lg me-1"></i> {{ $t('Close') }}
                </AppButton>
                <AppButton btnClass="btn btn-sm btn-soft-primary"  @click="copy">
                    <i class="bi bi-bookmarks me-1"></i> {{ $t('Copy') }}
                </AppButton>
            </template>
        </PersonalAccessTokensModal>
    </div>
</template>

<script>
import PersonalAccessTokensModal from "./PersonalAccessTokensModal.vue";
import AppButton from "../../shared/AppButton.vue";
import { ref } from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import moment from "moment";
import AppConfirm from "@/vue/shared/dialog/AppConfirm.vue";
import usePermissions from "@/vue/composables/usePermissions";
export default {
    /*
     * The component's data.
     */
    props: {
        user_id: {
            type: Number,
            required: true
        },
        auth_user: {
            type: Object,
            required: true,
        }
    },

    components:  {
        PersonalAccessTokensModal,
        AppButton
    },

    data() {
        return {
            accessToken: null,

            tokens: [],
            scopes: [],
            showPersonalAccessTokens: false,

            form: {
                name: '',
                scopes: [],
                errors: []
            }
        };
    },

    setup() {
        const createTokenModalActive = ref(false);
        const showTokenModalActive = ref(false);
        const isLoading = ref(false);
        const isPrecognition = ref(false);

        const toggleCreateTokenModal = () => {
            createTokenModalActive.value = !createTokenModalActive.value;
        };
        const toggleShowTokenModal = () => {
            showTokenModalActive.value = !showTokenModalActive.value;
        };
        const {reveal: confirm} = createConfirmDialog(AppConfirm);
        const { hasPermission } = usePermissions();
        return {
            createTokenModalActive,
            showTokenModalActive,
            toggleCreateTokenModal,
            toggleShowTokenModal,
            isLoading,
            confirm,
            hasPermission,
            moment
        };
    },


    /**
     * Prepare the component (Vue 2.x).
     */
    mounted() {
        this.prepareComponent();
    },

    watch: {
        user_id: {
            handler(newUserId, oldUserId) {
                if (newUserId !== oldUserId) {
                    this.resetComponentData();
                    this.prepareComponent();
                }
            }
        }
    },

    methods: {
        /**
         * Prepare the component.
         */
        prepareComponent() {
            this.getTokens(this.user_id);
            this.getScopes();
        },
        resetComponentData() {
            this.tokens = [];
            this.scopes = [];
            this.accessToken = null;
            this.form = {
                name: '',
                scopes: [],
                errors: []
            };
            this.isLoading = false;
        },

        copy() {
            this.$refs.tokenTextArea.focus();
            document.execCommand('copy');
        },

        validate() {
            if (this.form.name.length >= 3) {
                this.isPrecognition = true;
                this.store();
            }
        },

        async confirmWindow(title, message) {
            return await this.confirm({
                    title,
                    message,
                    confirmButtonText: $t("Ok"),
                    cancelButtonText: $t("Close")
                }
            );
        },

        /**
         * Get all of the personal access tokens for the user.
         */
        getTokens(user_id) {
            this.isLoading = true;
            axios.get(`/api/users/${user_id}/personal-access-tokens`)
                .then(response => {
                    this.tokens = response.data;
                    this.isLoading = false;
                })
                .catch(error => {
                    this.isLoading = false;
                });
        },

        /**
         * Get all of the available scopes.
         */
        getScopes() {
            this.isLoading = true;
            axios.get('/oauth/scopes')
                .then(response => {
                    this.scopes = response.data;
                    this.isLoading = false;
                })
                .catch(error => {
                    this.isLoading = false;
                });
        },

        save() {
            this.isPrecognition = false;
            this.store();
        },

        /**
         * Create a new personal access token.
         */
        async store() {
            if (this.isLoading || !this.form.name.trim().length) {
                return;
            }
            this.accessToken = null;

            this.form.errors = [];

            if (!this.isPrecognition) {
                this.isLoading = true;
            }
            try {
                if (this.isPrecognition) {
                    await axios.post(`/api/users/${this.user_id}/personal-access-tokens`, this.form, {
                        headers: {
                            'Precognition': true,
                            'Precognition-Validate-Only': 'name'
                        }
                    });
                    this.isPrecognition = false;
                    return;
                } else {
                    const response = await axios.post(`/api/users/${this.user_id}/personal-access-tokens`, this.form);
                    this.isLoading = false;
                    this.form.name = '';
                    this.form.scopes = [];
                    this.form.errors = [];

                    this.tokens.unshift(response.data.token);
                    this.accessToken = response.data.accessToken;
                    this.toggleCreateTokenModal();
                    this.toggleShowTokenModal();
                }
            } catch (error) {
                this.isPrecognition = false;
                this.isLoading = false;
                if (typeof error.response?.data === 'object') {
                    this.form.errors = [error.response.data.errors.name[0]];
                } else {
                    this.form.errors = [$t('Something went wrong. Please try again.')];
                }
            }
        },
        /**
         * Revoke the given token.
         */
        async revoke(user_id, token_id) {
            const response = await this.confirmWindow($t('Are you sure ?'), $t('messages.delete_api_token_warning'));
            if (response.isCanceled) {
                return;
            }
            this.isLoading = true;
            axios.delete(`/api/users/${user_id}/personal-access-tokens/${token_id}`)
                .then(response => {
                    this.getTokens(this.user_id);
                })
                .catch(error => {
                    this.isLoading = false;
                });
        }
    }
}
</script>

<style scoped>
.action-link {
    cursor: pointer;
}

.modal-content {
    display: flex;
    flex-direction: column;
}

</style>
